// Per-table delta channel: the row-level changes between two group positions
// for one member table. This is a NESTED accumulator (the group leads the
// delta; see ../rtable_group/delta.ts) keyed under the table's id in the root
// delta's `nested` map.
//
// The row channel emits a RowChange for a row IFF its enforced liveness flipped
// OR a WRITTEN (non-default) column value moved — i.e. only effects of row-ops
// or at-use voiding-verdict flips, all of which sit above the combined revision
// bound (at-use semantics make this floor exact: a causal-past schema/target
// can no longer revise an old row). Schema-default and table/column-drop effects
// are UNIFORM and never enumerated here; they live in the group's schema
// sub-delta channel, applied by the consumer to rows not present in the walk.
//
// Diffing uses deltaRowState (LWW written values, no default fallback) over the
// UNION of both horizons' schema columns; incarnation-scoped resolution keeps
// drop/re-add from surfacing stale per-row diffs. See ../rtable/view.ts.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { json } from "@hyper-hyper-space/hhs3_json";
import { tableOpsFromGroupPayload } from "../rtable_group/scopes.js";
function sameLiteral(a, b) {
    if (a === undefined || b === undefined)
        return a === b;
    return json.toStringNormalized(a) === json.toStringNormalized(b);
}
export class RTableDeltaAccumulator {
    constructor(table, start, end) {
        this.table = table;
        this.start = start;
        this.end = end;
        this.candidates = new Set();
    }
    // Collect every rowId this table touches in the walked entry (insert /
    // update / delete all matter: any could flip liveness or a written value).
    ingest(entry) {
        return __awaiter(this, void 0, void 0, function* () {
            const ops = tableOpsFromGroupPayload(entry.payload, this.table.getTableName());
            let touched = false;
            for (const op of ops) {
                this.candidates.add(op.rowId);
                touched = true;
            }
            return touched;
        });
    }
    finalize() {
        return __awaiter(this, void 0, void 0, function* () {
            const viewStart = yield this.table.getView(this.start, this.start);
            const viewEnd = yield this.table.getView(this.end, this.end);
            // diff over the union of both horizons' columns (schema-independent
            // written resolution keeps drops/defaults out of the per-row diff)
            const columns = [...new Set([
                    ...(yield viewStart.getColumns()),
                    ...(yield viewEnd.getColumns()),
                ])];
            const rowChanges = [];
            for (const rowId of [...this.candidates].sort()) {
                const before = yield viewStart.deltaRowState(rowId, columns);
                const after = yield viewEnd.deltaRowState(rowId, columns);
                const columnChanges = [];
                if (after.live) {
                    for (const column of columns) {
                        const b = before.live ? before.written[column] : undefined;
                        const a = after.written[column];
                        if (!sameLiteral(b, a))
                            columnChanges.push({ column, before: b, after: a });
                    }
                    columnChanges.sort((x, y) => (x.column < y.column ? -1 : x.column > y.column ? 1 : 0));
                }
                if (before.live === after.live && columnChanges.length === 0)
                    continue;
                rowChanges.push({
                    rowId,
                    liveBefore: before.live,
                    liveAfter: after.live,
                    author: after.live ? after.author : before.author,
                    columnChanges,
                });
            }
            return {
                type: this.table.getType(),
                changes: { rowChanges },
                nested: new Map(),
            };
        });
    }
}
